/**
 * Catálogo de permisos atómicos de Nexus.
 *
 * Diseño:
 * - Cada permiso es un string `dominio:recurso:accion` (o `dominio:accion`).
 * - Los roles son colecciones de estos permisos.
 * - El backend valida con `requirePermission(perm)`; el front oculta UI
 *   con `<Can permission="..." />`. La UI ocultada NO es seguridad,
 *   solo UX — la verdad vive en el backend.
 *
 * Convención: si un módulo distingue lectura de escritura, el de lectura
 * termina en `:view`. Las acciones modificadoras son verbos específicos.
 */

export const PERMISSIONS = {
  // ── Pulso ───────────────────────────────────────────────────────────
  PULSE_VIEW: "pulse:view",

  // ── Dashboard ───────────────────────────────────────────────────────
  DASHBOARD_EXECUTIVE_VIEW: "dashboard:executive:view",
  DASHBOARD_OPERATIONAL_VIEW: "dashboard:operational:view",
  DASHBOARD_ANALYTICAL_VIEW: "dashboard:analytical:view",

  // ── Liquidaciones (el módulo más sensible: máxima granularidad) ────
  SETTLEMENTS_VIEW: "settlements:view",
  SETTLEMENTS_GENERATE: "settlements:generate",
  SETTLEMENTS_MARK_TRANSFER: "settlements:mark_transfer",
  SETTLEMENTS_MARK_TRANSFER_FAILED: "settlements:mark_transfer_failed",
  SETTLEMENTS_RESOLVE_DISPUTE: "settlements:resolve_dispute",

  // ── Documentos tributarios (DTE / SII) ─────────────────────────────
  // Lectura y acción separadas: reencolar toca el carril tributario y
  // puede terminar en un folio consumido ante el SII.
  TAX_VIEW: "tax:view",
  TAX_RETRY: "tax:retry",
  // Editar el emisor afecta a TODO lo que se emita después: permiso aparte.
  TAX_CONFIGURE: "tax:configure",
  SETTLEMENTS_EDIT_PRODUCER_BANK: "settlements:edit_producer_bank_data",
  SETTLEMENTS_REPORTS_VIEW: "settlements:reports:view",
  SETTLEMENTS_REPORTS_EXPORT: "settlements:reports:export",

  // ── Governance: moderación de eventos ──────────────────────────────
  MODERATION_VIEW: "moderation:view",
  MODERATION_UNPUBLISH: "moderation:unpublish",
  MODERATION_FREEZE: "moderation:freeze",
  MODERATION_CANCEL: "moderation:cancel",
  MODERATION_BAN: "moderation:ban",

  // ── Governance: incidentes ─────────────────────────────────────────
  INCIDENTS_VIEW: "incidents:view",
  INCIDENTS_EXPORT: "incidents:export",

  // ── Governance: blacklist global ───────────────────────────────────
  BLACKLIST_VIEW: "blacklist:view",
  BLACKLIST_APPROVE_PROPOSAL: "blacklist:approve_proposal",
  BLACKLIST_REJECT_PROPOSAL: "blacklist:reject_proposal",
  BLACKLIST_REQUEST_INFO: "blacklist:request_info",
  BLACKLIST_APPEALS_RESOLVE: "blacklist:appeals:resolve",

  // ── Aplicaciones: productores ──────────────────────────────────────
  APPS_PRODUCERS_VIEW: "applications:producers:view",
  APPS_PRODUCERS_CONTACT: "applications:producers:contact",
  APPS_PRODUCERS_APPROVE: "applications:producers:approve",
  APPS_PRODUCERS_REJECT: "applications:producers:reject",
  APPS_PRODUCERS_EXPORT: "applications:producers:export",

  // ── Aplicaciones: artistas ─────────────────────────────────────────
  APPS_ARTISTS_VIEW: "applications:artists:view",
  APPS_ARTISTS_CONTACT: "applications:artists:contact",
  APPS_ARTISTS_APPROVE: "applications:artists:approve",
  APPS_ARTISTS_REJECT: "applications:artists:reject",
  APPS_ARTISTS_EXPORT: "applications:artists:export",

  // ── Aplicaciones: proveedores ──────────────────────────────────────
  APPS_PROVIDERS_VIEW: "applications:providers:view",
  APPS_PROVIDERS_VERIFY: "applications:providers:verify",
  APPS_PROVIDERS_REJECT: "applications:providers:reject",

  // ── Aplicaciones: trabajo ──────────────────────────────────────────
  APPS_JOBS_VIEW: "applications:jobs:view",
  APPS_JOBS_CONTACT: "applications:jobs:contact",
  APPS_JOBS_ARCHIVE: "applications:jobs:archive",

  // ── Comunicaciones ─────────────────────────────────────────────────
  COMMS_PUBLIC_MESSAGES_VIEW: "comms:public_messages:view",
  COMMS_PUBLIC_MESSAGES_REPLY: "comms:public_messages:reply",
  COMMS_PUBLIC_MESSAGES_ARCHIVE: "comms:public_messages:archive",
  COMMS_CONTACT_VIEW: "comms:contact:view",
  COMMS_CONTACT_REPLY: "comms:contact:reply",
  COMMS_CONTACT_ARCHIVE: "comms:contact:archive",
  COMMS_COMMUNITY_VIEW: "comms:community:view",

  // ── Operaciones ────────────────────────────────────────────────────
  OPS_MP_HEALTH_VIEW: "ops:mp_health:view",
  OPS_MP_HEALTH_FORCE_SWEEP: "ops:mp_health:force_sweep",
  OPS_MP_HEALTH_RECONCILE: "ops:mp_health:reconcile_by_id",
  OPS_MP_AUDIT_VIEW: "ops:mp_audit:view",
  OPS_MP_AUDIT_INSPECT: "ops:mp_audit:inspect",
  OPS_FEATURES_VIEW: "ops:features:view",
  OPS_FEATURES_TOGGLE: "ops:features:toggle",
  OPS_DATA_VIEW: "ops:data:view",
  OPS_DATA_EXPORT: "ops:data:export",

  // ── Tema ───────────────────────────────────────────────────────────
  THEME_VIEW: "theme:view",
  THEME_EDIT_DRAFT: "theme:edit_draft",
  THEME_PUBLISH: "theme:publish",
  THEME_ACTIVATE: "theme:activate",

  // ── Gestión de productores (cuentas, clubs, eventos, staff/RBAC) ───
  // Permite ver, editar y borrar productores en cascade. Pensado para
  // pruebas/QA mientras Mixcore está pre-launch — hard-delete bloquea
  // si hay tickets pagados (proteger data económica real). Cada acción
  // queda en NexusAuditLog.
  PRODUCERS_MGMT_VIEW: "producers_mgmt:view",
  PRODUCERS_MGMT_EDIT: "producers_mgmt:edit",
  PRODUCERS_MGMT_SOFT_DELETE: "producers_mgmt:soft_delete",
  PRODUCERS_MGMT_HARD_DELETE: "producers_mgmt:hard_delete",
  PRODUCERS_MGMT_MANAGE_CLUBS: "producers_mgmt:manage_clubs",
  PRODUCERS_MGMT_MANAGE_EVENTS: "producers_mgmt:manage_events",
  PRODUCERS_MGMT_MANAGE_STAFF: "producers_mgmt:manage_staff",

  // ── Gestión de artistas (cuentas + perfil de catálogo) ─────────────
  // Espejo de producers_mgmt: ver el catálogo, alta manual, editar flags
  // y perfil de booking, borrar (soft). Sin hard-delete en la v1.
  ARTISTS_MGMT_VIEW: "artists_mgmt:view",
  ARTISTS_MGMT_EDIT: "artists_mgmt:edit",
  ARTISTS_MGMT_SOFT_DELETE: "artists_mgmt:soft_delete",

  // ── Documentación ──────────────────────────────────────────────────
  DOCS_VIEW: "docs:view",

  // ── Cuenta demo (showroom para socios comerciales) ─────────────────
  // DEMO_VIEW: ver status de la cuenta demo en Nexus.
  // DEMO_RESET: borrar contenido publicado por socios y recrear placeholders.
  // DEMO_SEED: crear la cuenta demo por primera vez. Owner-only.
  DEMO_VIEW: "demo:view",
  DEMO_RESET: "demo:reset",
  DEMO_SEED: "demo:seed",
  // DEMO_REVEAL_PASSWORD: ver el password de la cuenta demo en plano.
  // Owner-only. Otros roles con DEMO_VIEW siguen viendo status pero no
  // pueden copiar credenciales — necesitan pedirle al Owner.
  DEMO_REVEAL_PASSWORD: "demo:reveal_password",

  // ── Nexus Control (gestión de usuarios y permisos del propio Nexus)
  NEXUS_CONTROL_VIEW: "nexus_control:view",
  NEXUS_CONTROL_MANAGE_USERS: "nexus_control:manage_users",
  NEXUS_CONTROL_MANAGE_ROLES: "nexus_control:manage_roles",
  NEXUS_CONTROL_VIEW_AUDIT: "nexus_control:view_audit_log",

  // ── Feed cultural / Comunidad (Docs/plan-comercial/03b) ────────────
  CONTENT_VIEW: "content:view",
  CONTENT_MANAGE: "content:manage",
  CONTENT_PUBLISH: "content:publish",

  // ── Facturación / SKUs / entitlements (Fase 0) ─────────────────────
  BILLING_SKUS_VIEW: "billing:skus:view",
  BILLING_SKUS_EDIT: "billing:skus:edit",
  BILLING_ENTITLEMENTS_VIEW: "billing:entitlements:view",
  BILLING_ENTITLEMENTS_MANAGE: "billing:entitlements:manage",

  // ── Códigos promocionales (gratis-total / %desc para módulos Pro) ──
  PROMO_CODES_VIEW: "promo_codes:view",
  PROMO_CODES_MANAGE: "promo_codes:manage",

  // ── Newsletter (#14) ───────────────────────────────────────────────
  NEWSLETTER_MANAGE: "newsletter:manage",

  // ── Seguimiento (CRM de prospectos + Kanban de tareas + validación) ─
  SEGUIMIENTO_VIEW: "seguimiento:view",
  SEGUIMIENTO_MANAGE: "seguimiento:manage",

  // ── Programa de Reclutadores Comerciales (F9) ──────────────────────
  // VIEW/MANAGE/PAY = gestión de Mixcore; SELF = el propio reclutador
  // (sólo ve lo suyo). SELF se evalúa EXPLÍCITO en el menú: el wildcard
  // del Owner no lo convierte en reclutador.
  RECRUITERS_VIEW: "recruiters:view",
  RECRUITERS_MANAGE: "recruiters:manage",
  RECRUITERS_PAY: "recruiters:pay",
  RECRUITERS_SELF: "recruiters:self",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Wildcard especial. El backend lo interpreta como "todo". Solo el rol
 * Owner lo tiene. NO usar en ningún otro rol.
 */
export const PERMISSION_ALL = "*" as const;

/**
 * Roles preset. Pueden duplicarse y editarse en Nexus Control. El backend
 * los siembra al primer boot si la tabla está vacía.
 */
export const PRESET_ROLES = {
  OWNER: {
    slug: "owner",
    name: "Owner",
    description: "Acceso total a Nexus. Único rol que puede gestionar Nexus Control. No eliminable.",
    isSystem: true,
    permissions: [PERMISSION_ALL] as readonly string[],
  },
  ADMIN: {
    slug: "admin",
    name: "Admin",
    description: "Casi todo menos Feature Flags y Nexus Control.",
    isSystem: true,
    permissions: [
      PERMISSIONS.PULSE_VIEW,
      PERMISSIONS.DASHBOARD_EXECUTIVE_VIEW,
      PERMISSIONS.DASHBOARD_OPERATIONAL_VIEW,
      PERMISSIONS.DASHBOARD_ANALYTICAL_VIEW,
      PERMISSIONS.SETTLEMENTS_VIEW,
      PERMISSIONS.SETTLEMENTS_REPORTS_VIEW,
      PERMISSIONS.MODERATION_VIEW,
      PERMISSIONS.MODERATION_UNPUBLISH,
      PERMISSIONS.MODERATION_FREEZE,
      PERMISSIONS.INCIDENTS_VIEW,
      PERMISSIONS.BLACKLIST_VIEW,
      PERMISSIONS.BLACKLIST_APPROVE_PROPOSAL,
      PERMISSIONS.BLACKLIST_REJECT_PROPOSAL,
      PERMISSIONS.BLACKLIST_REQUEST_INFO,
      PERMISSIONS.BLACKLIST_APPEALS_RESOLVE,
      PERMISSIONS.APPS_PRODUCERS_VIEW,
      PERMISSIONS.APPS_PRODUCERS_CONTACT,
      PERMISSIONS.APPS_PRODUCERS_APPROVE,
      PERMISSIONS.APPS_PRODUCERS_REJECT,
      PERMISSIONS.APPS_PRODUCERS_EXPORT,
      PERMISSIONS.APPS_ARTISTS_VIEW,
      PERMISSIONS.APPS_ARTISTS_CONTACT,
      PERMISSIONS.APPS_ARTISTS_APPROVE,
      PERMISSIONS.APPS_ARTISTS_REJECT,
      PERMISSIONS.APPS_ARTISTS_EXPORT,
      PERMISSIONS.APPS_PROVIDERS_VIEW,
      PERMISSIONS.APPS_PROVIDERS_VERIFY,
      PERMISSIONS.APPS_PROVIDERS_REJECT,
      PERMISSIONS.APPS_JOBS_VIEW,
      PERMISSIONS.APPS_JOBS_CONTACT,
      PERMISSIONS.APPS_JOBS_ARCHIVE,
      PERMISSIONS.COMMS_PUBLIC_MESSAGES_VIEW,
      PERMISSIONS.COMMS_PUBLIC_MESSAGES_REPLY,
      PERMISSIONS.COMMS_PUBLIC_MESSAGES_ARCHIVE,
      PERMISSIONS.COMMS_CONTACT_VIEW,
      PERMISSIONS.COMMS_CONTACT_REPLY,
      PERMISSIONS.COMMS_CONTACT_ARCHIVE,
      PERMISSIONS.COMMS_COMMUNITY_VIEW,
      PERMISSIONS.OPS_MP_HEALTH_VIEW,
      PERMISSIONS.OPS_MP_HEALTH_FORCE_SWEEP,
      PERMISSIONS.OPS_MP_HEALTH_RECONCILE,
      PERMISSIONS.OPS_MP_AUDIT_VIEW,
      PERMISSIONS.OPS_MP_AUDIT_INSPECT,
      PERMISSIONS.OPS_DATA_VIEW,
      PERMISSIONS.OPS_DATA_EXPORT,
      PERMISSIONS.THEME_VIEW,
      PERMISSIONS.THEME_EDIT_DRAFT,
      // Admin puede gestionar productores excepto hard-delete (Owner only).
      PERMISSIONS.PRODUCERS_MGMT_VIEW,
      PERMISSIONS.PRODUCERS_MGMT_EDIT,
      PERMISSIONS.PRODUCERS_MGMT_SOFT_DELETE,
      PERMISSIONS.PRODUCERS_MGMT_MANAGE_CLUBS,
      PERMISSIONS.PRODUCERS_MGMT_MANAGE_EVENTS,
      PERMISSIONS.PRODUCERS_MGMT_MANAGE_STAFF,
      // Gestión de artistas (alta manual + edición de perfil).
      PERMISSIONS.ARTISTS_MGMT_VIEW,
      PERMISSIONS.ARTISTS_MGMT_EDIT,
      PERMISSIONS.ARTISTS_MGMT_SOFT_DELETE,
      PERMISSIONS.DOCS_VIEW,
      PERMISSIONS.SEGUIMIENTO_VIEW,
      PERMISSIONS.SEGUIMIENTO_MANAGE,
    ] as readonly string[],
  },
  FINANCE: {
    slug: "finance",
    name: "Finanzas",
    description: "Liquidaciones, reportes, auditoría MP.",
    isSystem: true,
    permissions: [
      PERMISSIONS.PULSE_VIEW,
      PERMISSIONS.DASHBOARD_EXECUTIVE_VIEW,
      PERMISSIONS.DASHBOARD_OPERATIONAL_VIEW,
      PERMISSIONS.DASHBOARD_ANALYTICAL_VIEW,
      PERMISSIONS.SETTLEMENTS_VIEW,
      PERMISSIONS.SETTLEMENTS_GENERATE,
      PERMISSIONS.SETTLEMENTS_MARK_TRANSFER,
      PERMISSIONS.SETTLEMENTS_MARK_TRANSFER_FAILED,
      PERMISSIONS.SETTLEMENTS_RESOLVE_DISPUTE,
      PERMISSIONS.SETTLEMENTS_EDIT_PRODUCER_BANK,
      PERMISSIONS.SETTLEMENTS_REPORTS_VIEW,
      PERMISSIONS.SETTLEMENTS_REPORTS_EXPORT,
      PERMISSIONS.OPS_MP_HEALTH_VIEW,
      PERMISSIONS.OPS_MP_HEALTH_FORCE_SWEEP,
      PERMISSIONS.OPS_MP_HEALTH_RECONCILE,
      PERMISSIONS.OPS_MP_AUDIT_VIEW,
      PERMISSIONS.OPS_MP_AUDIT_INSPECT,
      PERMISSIONS.OPS_DATA_VIEW,
      PERMISSIONS.DOCS_VIEW,
    ] as readonly string[],
  },
  MODERATOR: {
    slug: "moderator",
    name: "Moderador",
    description: "Modera contenido y aplicaciones; sin acceso a finanzas.",
    isSystem: true,
    permissions: [
      PERMISSIONS.PULSE_VIEW,
      PERMISSIONS.DASHBOARD_EXECUTIVE_VIEW,
      PERMISSIONS.DASHBOARD_OPERATIONAL_VIEW,
      PERMISSIONS.MODERATION_VIEW,
      PERMISSIONS.MODERATION_UNPUBLISH,
      PERMISSIONS.MODERATION_FREEZE,
      PERMISSIONS.MODERATION_CANCEL,
      PERMISSIONS.INCIDENTS_VIEW,
      PERMISSIONS.BLACKLIST_VIEW,
      PERMISSIONS.BLACKLIST_APPROVE_PROPOSAL,
      PERMISSIONS.BLACKLIST_REJECT_PROPOSAL,
      PERMISSIONS.BLACKLIST_REQUEST_INFO,
      PERMISSIONS.BLACKLIST_APPEALS_RESOLVE,
      PERMISSIONS.APPS_PRODUCERS_VIEW,
      PERMISSIONS.APPS_PRODUCERS_CONTACT,
      PERMISSIONS.APPS_PRODUCERS_APPROVE,
      PERMISSIONS.APPS_PRODUCERS_REJECT,
      PERMISSIONS.APPS_ARTISTS_VIEW,
      PERMISSIONS.APPS_ARTISTS_CONTACT,
      PERMISSIONS.APPS_ARTISTS_APPROVE,
      PERMISSIONS.APPS_ARTISTS_REJECT,
      PERMISSIONS.APPS_PROVIDERS_VIEW,
      PERMISSIONS.APPS_PROVIDERS_VERIFY,
      PERMISSIONS.APPS_PROVIDERS_REJECT,
      PERMISSIONS.APPS_JOBS_VIEW,
      PERMISSIONS.APPS_JOBS_CONTACT,
      PERMISSIONS.APPS_JOBS_ARCHIVE,
      PERMISSIONS.COMMS_PUBLIC_MESSAGES_VIEW,
      PERMISSIONS.COMMS_PUBLIC_MESSAGES_REPLY,
      PERMISSIONS.COMMS_PUBLIC_MESSAGES_ARCHIVE,
      PERMISSIONS.COMMS_CONTACT_VIEW,
      PERMISSIONS.COMMS_CONTACT_REPLY,
      PERMISSIONS.COMMS_CONTACT_ARCHIVE,
      PERMISSIONS.COMMS_COMMUNITY_VIEW,
      PERMISSIONS.DOCS_VIEW,
    ] as readonly string[],
  },
  SUPPORT: {
    slug: "support",
    name: "Soporte",
    description: "Comunicaciones + vista de aplicaciones; sin acciones críticas.",
    isSystem: true,
    permissions: [
      PERMISSIONS.PULSE_VIEW,
      PERMISSIONS.DASHBOARD_EXECUTIVE_VIEW,
      PERMISSIONS.DASHBOARD_OPERATIONAL_VIEW,
      PERMISSIONS.APPS_PRODUCERS_VIEW,
      PERMISSIONS.APPS_ARTISTS_VIEW,
      PERMISSIONS.APPS_PROVIDERS_VIEW,
      PERMISSIONS.APPS_JOBS_VIEW,
      PERMISSIONS.APPS_JOBS_CONTACT,
      PERMISSIONS.COMMS_PUBLIC_MESSAGES_VIEW,
      PERMISSIONS.COMMS_PUBLIC_MESSAGES_REPLY,
      PERMISSIONS.COMMS_CONTACT_VIEW,
      PERMISSIONS.COMMS_CONTACT_REPLY,
      PERMISSIONS.COMMS_COMMUNITY_VIEW,
      PERMISSIONS.OPS_MP_HEALTH_VIEW,
      PERMISSIONS.PRODUCERS_MGMT_VIEW,
      PERMISSIONS.ARTISTS_MGMT_VIEW,
      PERMISSIONS.DOCS_VIEW,
    ] as readonly string[],
  },
  DESIGNER: {
    slug: "designer",
    name: "Diseñador",
    description: "Edita borradores de tema; no puede publicar ni activar.",
    isSystem: true,
    permissions: [
      PERMISSIONS.PULSE_VIEW,
      PERMISSIONS.THEME_VIEW,
      PERMISSIONS.THEME_EDIT_DRAFT,
      PERMISSIONS.DOCS_VIEW,
    ] as readonly string[],
  },
  VIEWER: {
    slug: "viewer",
    name: "Solo lectura",
    description: "Acceso de vista a casi todo; sin acciones modificadoras.",
    isSystem: true,
    permissions: [
      PERMISSIONS.PULSE_VIEW,
      PERMISSIONS.DASHBOARD_EXECUTIVE_VIEW,
      PERMISSIONS.DASHBOARD_OPERATIONAL_VIEW,
      PERMISSIONS.DASHBOARD_ANALYTICAL_VIEW,
      PERMISSIONS.SETTLEMENTS_VIEW,
      PERMISSIONS.SETTLEMENTS_REPORTS_VIEW,
      PERMISSIONS.MODERATION_VIEW,
      PERMISSIONS.INCIDENTS_VIEW,
      PERMISSIONS.BLACKLIST_VIEW,
      PERMISSIONS.APPS_PRODUCERS_VIEW,
      PERMISSIONS.APPS_ARTISTS_VIEW,
      PERMISSIONS.APPS_PROVIDERS_VIEW,
      PERMISSIONS.APPS_JOBS_VIEW,
      PERMISSIONS.COMMS_PUBLIC_MESSAGES_VIEW,
      PERMISSIONS.COMMS_CONTACT_VIEW,
      PERMISSIONS.COMMS_COMMUNITY_VIEW,
      PERMISSIONS.OPS_MP_AUDIT_VIEW,
      PERMISSIONS.THEME_VIEW,
      PERMISSIONS.PRODUCERS_MGMT_VIEW,
      PERMISSIONS.ARTISTS_MGMT_VIEW,
      PERMISSIONS.DOCS_VIEW,
      PERMISSIONS.SEGUIMIENTO_VIEW,
    ] as readonly string[],
  },
  SEGUIMIENTO: {
    slug: "seguimiento",
    name: "Seguimiento",
    description:
      "Acceso acotado SOLO a la sección Seguimiento (CRM de prospectos, tablero de tareas y validación de módulos). Para el equipo comercial sin acceso al resto del back-office.",
    isSystem: true,
    permissions: [
      PERMISSIONS.SEGUIMIENTO_VIEW,
      PERMISSIONS.SEGUIMIENTO_MANAGE,
    ] as readonly string[],
  },
  PRODUCTORES: {
    slug: "productores",
    name: "Productores",
    description:
      "Gestión de productores: ver, editar, borrar (soft) y administrar clubs/eventos/staff. Hard-delete reservado al Owner. Pensado para combinar con el rol Seguimiento.",
    isSystem: true,
    permissions: [
      PERMISSIONS.PRODUCERS_MGMT_VIEW,
      PERMISSIONS.PRODUCERS_MGMT_EDIT,
      PERMISSIONS.PRODUCERS_MGMT_SOFT_DELETE,
      PERMISSIONS.PRODUCERS_MGMT_MANAGE_CLUBS,
      PERMISSIONS.PRODUCERS_MGMT_MANAGE_EVENTS,
      PERMISSIONS.PRODUCERS_MGMT_MANAGE_STAFF,
    ] as readonly string[],
  },
  RECLUTADORES: {
    slug: "reclutadores",
    name: "Reclutadores (gestión)",
    description:
      "Programa de Reclutadores Comerciales: registrar reclutadores, asignar productores del CRM, revisar el checklist de cada evento, generar liquidaciones y registrar pagos.",
    isSystem: true,
    permissions: [
      PERMISSIONS.RECRUITERS_VIEW,
      PERMISSIONS.RECRUITERS_MANAGE,
      PERMISSIONS.RECRUITERS_PAY,
    ] as readonly string[],
  },
  RECLUTADOR: {
    slug: "reclutador",
    name: "Reclutador",
    description:
      "Reclutador comercial: sólo su espacio de trabajo (cartera, checklist de sus eventos y ganancias). Se asigna solo al registrarlo en Nexus → Reclutadores.",
    isSystem: true,
    permissions: [PERMISSIONS.RECRUITERS_SELF] as readonly string[],
  },
  ARTISTAS: {
    slug: "artistas",
    name: "Artistas",
    description:
      "Gestión de artistas: ver el catálogo, dar de alta artistas manualmente, editar su perfil de booking y borrarlos (soft). Para el equipo que hace el onboarding de artistas.",
    isSystem: true,
    permissions: [
      PERMISSIONS.ARTISTS_MGMT_VIEW,
      PERMISSIONS.ARTISTS_MGMT_EDIT,
      PERMISSIONS.ARTISTS_MGMT_SOFT_DELETE,
    ] as readonly string[],
  },
} as const;

export type PresetRoleSlug = keyof typeof PRESET_ROLES;

/**
 * Agrupación de permisos para la UI de Nexus Control (matriz de permisos).
 * El backend ignora estos grupos; son puramente cosméticos.
 */
export const PERMISSION_GROUPS = [
  {
    label: "Pulso",
    permissions: [{ key: PERMISSIONS.PULSE_VIEW, label: "Ver pulso" }],
  },
  {
    label: "Dashboard",
    permissions: [
      { key: PERMISSIONS.DASHBOARD_EXECUTIVE_VIEW, label: "Ver vista ejecutiva" },
      { key: PERMISSIONS.DASHBOARD_OPERATIONAL_VIEW, label: "Ver vista operativa" },
      { key: PERMISSIONS.DASHBOARD_ANALYTICAL_VIEW, label: "Ver vista analítica" },
    ],
  },
  {
    label: "Liquidaciones",
    permissions: [
      { key: PERMISSIONS.SETTLEMENTS_VIEW, label: "Ver liquidaciones" },
      { key: PERMISSIONS.SETTLEMENTS_GENERATE, label: "Generar liquidación" },
      { key: PERMISSIONS.SETTLEMENTS_MARK_TRANSFER, label: "Marcar como transferido" },
      { key: PERMISSIONS.SETTLEMENTS_MARK_TRANSFER_FAILED, label: "Marcar transferencia fallida" },
      { key: PERMISSIONS.SETTLEMENTS_RESOLVE_DISPUTE, label: "Resolver disputa" },
      { key: PERMISSIONS.SETTLEMENTS_EDIT_PRODUCER_BANK, label: "Editar datos bancarios productor" },
      { key: PERMISSIONS.SETTLEMENTS_REPORTS_VIEW, label: "Ver reportes mensuales" },
      { key: PERMISSIONS.SETTLEMENTS_REPORTS_EXPORT, label: "Exportar reportes" },
    ],
  },
  {
    label: "Governance — Moderación",
    permissions: [
      { key: PERMISSIONS.MODERATION_VIEW, label: "Ver eventos a moderar" },
      { key: PERMISSIONS.MODERATION_UNPUBLISH, label: "Despublicar evento" },
      { key: PERMISSIONS.MODERATION_FREEZE, label: "Congelar evento" },
      { key: PERMISSIONS.MODERATION_CANCEL, label: "Cancelar evento (irreversible)" },
      { key: PERMISSIONS.MODERATION_BAN, label: "Banear evento (irreversible)" },
    ],
  },
  {
    label: "Governance — Incidentes",
    permissions: [
      { key: PERMISSIONS.INCIDENTS_VIEW, label: "Ver incidentes" },
      { key: PERMISSIONS.INCIDENTS_EXPORT, label: "Exportar incidentes" },
    ],
  },
  {
    label: "Governance — Blacklist global",
    permissions: [
      { key: PERMISSIONS.BLACKLIST_VIEW, label: "Ver propuestas" },
      { key: PERMISSIONS.BLACKLIST_APPROVE_PROPOSAL, label: "Aprobar propuesta" },
      { key: PERMISSIONS.BLACKLIST_REJECT_PROPOSAL, label: "Rechazar propuesta" },
      { key: PERMISSIONS.BLACKLIST_REQUEST_INFO, label: "Pedir más información" },
      { key: PERMISSIONS.BLACKLIST_APPEALS_RESOLVE, label: "Resolver apelaciones" },
    ],
  },
  {
    label: "Aplicaciones — Productores",
    permissions: [
      { key: PERMISSIONS.APPS_PRODUCERS_VIEW, label: "Ver postulaciones" },
      { key: PERMISSIONS.APPS_PRODUCERS_CONTACT, label: "Marcar como contactado" },
      { key: PERMISSIONS.APPS_PRODUCERS_APPROVE, label: "Aprobar productor" },
      { key: PERMISSIONS.APPS_PRODUCERS_REJECT, label: "Rechazar postulación" },
      { key: PERMISSIONS.APPS_PRODUCERS_EXPORT, label: "Exportar CSV" },
    ],
  },
  {
    label: "Aplicaciones — Artistas",
    permissions: [
      { key: PERMISSIONS.APPS_ARTISTS_VIEW, label: "Ver postulaciones" },
      { key: PERMISSIONS.APPS_ARTISTS_CONTACT, label: "Marcar como contactado" },
      { key: PERMISSIONS.APPS_ARTISTS_APPROVE, label: "Aprobar artista" },
      { key: PERMISSIONS.APPS_ARTISTS_REJECT, label: "Rechazar postulación" },
      { key: PERMISSIONS.APPS_ARTISTS_EXPORT, label: "Exportar CSV" },
    ],
  },
  {
    label: "Aplicaciones — Proveedores",
    permissions: [
      { key: PERMISSIONS.APPS_PROVIDERS_VIEW, label: "Ver proveedores" },
      { key: PERMISSIONS.APPS_PROVIDERS_VERIFY, label: "Verificar/activar" },
      { key: PERMISSIONS.APPS_PROVIDERS_REJECT, label: "Rechazar" },
    ],
  },
  {
    label: "Aplicaciones — Trabajo",
    permissions: [
      { key: PERMISSIONS.APPS_JOBS_VIEW, label: "Ver aplicaciones" },
      { key: PERMISSIONS.APPS_JOBS_CONTACT, label: "Marcar como contactado" },
      { key: PERMISSIONS.APPS_JOBS_ARCHIVE, label: "Archivar" },
    ],
  },
  {
    label: "Comunicaciones",
    permissions: [
      { key: PERMISSIONS.COMMS_PUBLIC_MESSAGES_VIEW, label: "Ver mensajes públicos" },
      { key: PERMISSIONS.COMMS_PUBLIC_MESSAGES_REPLY, label: "Responder mensajes" },
      { key: PERMISSIONS.COMMS_PUBLIC_MESSAGES_ARCHIVE, label: "Archivar mensajes" },
      { key: PERMISSIONS.COMMS_CONTACT_VIEW, label: "Ver contacto público" },
      { key: PERMISSIONS.COMMS_CONTACT_REPLY, label: "Responder contacto" },
      { key: PERMISSIONS.COMMS_CONTACT_ARCHIVE, label: "Archivar contacto" },
      { key: PERMISSIONS.COMMS_COMMUNITY_VIEW, label: "Ver comunidad" },
    ],
  },
  {
    label: "Operaciones — MP",
    permissions: [
      { key: PERMISSIONS.OPS_MP_HEALTH_VIEW, label: "Ver salud MP" },
      { key: PERMISSIONS.OPS_MP_HEALTH_FORCE_SWEEP, label: "Forzar sweep" },
      { key: PERMISSIONS.OPS_MP_HEALTH_RECONCILE, label: "Reconciliar por ID" },
      { key: PERMISSIONS.OPS_MP_AUDIT_VIEW, label: "Ver auditoría MP" },
      { key: PERMISSIONS.OPS_MP_AUDIT_INSPECT, label: "Inspeccionar payment_id" },
    ],
  },
  {
    label: "Operaciones — Plataforma",
    permissions: [
      { key: PERMISSIONS.OPS_FEATURES_VIEW, label: "Ver feature flags" },
      { key: PERMISSIONS.OPS_FEATURES_TOGGLE, label: "Activar/desactivar features" },
      { key: PERMISSIONS.OPS_DATA_VIEW, label: "Explorador de datos" },
      { key: PERMISSIONS.OPS_DATA_EXPORT, label: "Exportar datos" },
    ],
  },
  {
    label: "Tema",
    permissions: [
      { key: PERMISSIONS.THEME_VIEW, label: "Ver plantillas" },
      { key: PERMISSIONS.THEME_EDIT_DRAFT, label: "Editar borradores" },
      { key: PERMISSIONS.THEME_PUBLISH, label: "Publicar plantilla" },
      { key: PERMISSIONS.THEME_ACTIVATE, label: "Activar plantilla en producción" },
    ],
  },
  {
    label: "Gestión de productores",
    permissions: [
      { key: PERMISSIONS.PRODUCERS_MGMT_VIEW, label: "Ver productores y su data" },
      { key: PERMISSIONS.PRODUCERS_MGMT_EDIT, label: "Editar flags del productor (isBlocked, isDemoMode)" },
      { key: PERMISSIONS.PRODUCERS_MGMT_SOFT_DELETE, label: "Borrar productor (soft, reversible)" },
      { key: PERMISSIONS.PRODUCERS_MGMT_HARD_DELETE, label: "Borrar productor DEFINITIVAMENTE (cascade)" },
      { key: PERMISSIONS.PRODUCERS_MGMT_MANAGE_CLUBS, label: "Gestionar clubs del productor" },
      { key: PERMISSIONS.PRODUCERS_MGMT_MANAGE_EVENTS, label: "Gestionar eventos del productor" },
      { key: PERMISSIONS.PRODUCERS_MGMT_MANAGE_STAFF, label: "Gestionar staff/RBAC del club" },
    ],
  },
  {
    label: "Gestión de artistas",
    permissions: [
      { key: PERMISSIONS.ARTISTS_MGMT_VIEW, label: "Ver artistas y su ficha de catálogo" },
      { key: PERMISSIONS.ARTISTS_MGMT_EDIT, label: "Alta manual + editar flags y perfil de booking" },
      { key: PERMISSIONS.ARTISTS_MGMT_SOFT_DELETE, label: "Borrar artista (soft, reversible)" },
    ],
  },
  {
    label: "Documentación",
    permissions: [{ key: PERMISSIONS.DOCS_VIEW, label: "Ver documentación" }],
  },
  {
    label: "Nexus Control",
    permissions: [
      { key: PERMISSIONS.NEXUS_CONTROL_VIEW, label: "Ver Nexus Control" },
      { key: PERMISSIONS.NEXUS_CONTROL_MANAGE_USERS, label: "Gestionar usuarios" },
      { key: PERMISSIONS.NEXUS_CONTROL_MANAGE_ROLES, label: "Gestionar roles" },
      { key: PERMISSIONS.NEXUS_CONTROL_VIEW_AUDIT, label: "Ver audit log" },
    ],
  },
  {
    label: "Comunidad — Feed cultural",
    permissions: [
      { key: PERMISSIONS.CONTENT_VIEW, label: "Ver contenido" },
      { key: PERMISSIONS.CONTENT_MANAGE, label: "Crear/editar/eliminar contenido" },
      { key: PERMISSIONS.CONTENT_PUBLISH, label: "Publicar y destacar" },
    ],
  },
  {
    label: "Facturación",
    permissions: [
      { key: PERMISSIONS.BILLING_SKUS_VIEW, label: "Ver precios y SKUs" },
      { key: PERMISSIONS.BILLING_SKUS_EDIT, label: "Editar precios y SKUs" },
      { key: PERMISSIONS.BILLING_ENTITLEMENTS_VIEW, label: "Ver suscripciones/entitlements" },
      { key: PERMISSIONS.BILLING_ENTITLEMENTS_MANAGE, label: "Otorgar/revocar manualmente" },
      { key: PERMISSIONS.PROMO_CODES_VIEW, label: "Ver códigos promocionales + canjes" },
      { key: PERMISSIONS.PROMO_CODES_MANAGE, label: "Crear/editar/desactivar códigos promo" },
    ],
  },
  {
    label: "Newsletter",
    permissions: [
      { key: PERMISSIONS.NEWSLETTER_MANAGE, label: "Crear y enviar ediciones de newsletter" },
    ],
  },
  {
    label: "Seguimiento",
    permissions: [
      { key: PERMISSIONS.SEGUIMIENTO_VIEW, label: "Ver seguimiento (funnel, activos, tareas, métricas)" },
      { key: PERMISSIONS.SEGUIMIENTO_MANAGE, label: "Gestionar prospectos, tareas y validaciones" },
    ],
  },
  {
    label: "Reclutadores",
    permissions: [
      { key: PERMISSIONS.RECRUITERS_VIEW, label: "Ver reclutadores, cartera, checklist y comisiones" },
      { key: PERMISSIONS.RECRUITERS_MANAGE, label: "Registrar reclutadores, asignar productores, revisar checklist y configurar" },
      { key: PERMISSIONS.RECRUITERS_PAY, label: "Liquidaciones, ajustes, pagos y datos bancarios" },
      { key: PERMISSIONS.RECRUITERS_SELF, label: "Espacio del reclutador (sólo lo suyo)" },
    ],
  },
] as const;
